import { useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  updatePlayerAttendanceData,
  updatePlayerAttendanceStatus,
} from '@sportninja/common/services/game/attendanceServices'
import {
  promoteAvailabilityRequest,
  demoteAvailabilityRequest,
  withdrawAvailabilityRequest,
} from '@sportninja/common/services/game/availabilityRequestService'

interface Params {
  gameId?: string
  rosterId?: string
}

interface GoalieRef {
  id: string
}

export const useAttendanceMutations = ({ gameId, rosterId }: Params) => {
  const queryClient = useQueryClient()
  const { message } = App.useApp()

  const invalidatePlayers = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.gameRosterPlayers.byRoster(gameId, rosterId),
    })

  const updateAttendanceStatus = async (
    playerId: string,
    status: number | null
  ): Promise<void> => {
    try {
      await updatePlayerAttendanceStatus(
        gameId as string,
        rosterId as string,
        playerId,
        status
      )
      await invalidatePlayers()
    } catch (e) {
      message.error(
        `An error occurred while updating attendance: ${(e as Error).message}`
      )
      throw e
    }
  }

  const promoteSpare = async (requestId: string): Promise<boolean> => {
    try {
      await promoteAvailabilityRequest({ gameId: gameId as string, requestId })
      await invalidatePlayers()
      return true
    } catch (e) {
      message.error(`Unable to promote: ${(e as Error).message}`)
      return false
    }
  }

  const demoteSpare = async (requestId: string): Promise<boolean> => {
    try {
      await demoteAvailabilityRequest({ gameId: gameId as string, requestId })
      await invalidatePlayers()
      return true
    } catch (e) {
      message.error(`Unable to remove: ${(e as Error).message}`)
      return false
    }
  }

  const withdrawSpare = async (requestId: string): Promise<boolean> => {
    try {
      await withdrawAvailabilityRequest({ gameId: gameId as string, requestId })
      await invalidatePlayers()
      // The request is deleted, so the player can be asked again — they come
      // back to the Player Finder list.
      queryClient.invalidateQueries({
        queryKey: queryKeys.spareCandidates.all,
      })
      return true
    } catch (e) {
      message.error(`Unable to withdraw: ${(e as Error).message}`)
      return false
    }
  }

  const updatePlayerNumber = async (
    playerId: string,
    number: number
  ): Promise<boolean> => {
    try {
      await updatePlayerAttendanceData(
        gameId as string,
        rosterId as string,
        playerId,
        { player_number: number }
      )
      await invalidatePlayers()
      return true
    } catch (e) {
      message.error(`Unable to update number: ${(e as Error).message}`)
      return false
    }
  }

  const updateStartingChange = async (
    playerId: string,
    isStarting: boolean,
    _positionId?: string,
    otherGoaliesThatArePlaying: GoalieRef[] = []
  ): Promise<boolean> => {
    try {
      await Promise.all(
        otherGoaliesThatArePlaying.map((g) =>
          updatePlayerAttendanceData(
            gameId as string,
            rosterId as string,
            g.id,
            {
              is_proposed_starting: false,
            }
          )
        )
      )
      await updatePlayerAttendanceData(
        gameId as string,
        rosterId as string,
        playerId,
        { is_proposed_starting: isStarting }
      )
      await invalidatePlayers()
      return true
    } catch (e) {
      message.error(`Unable to update starting: ${(e as Error).message}`)
      return false
    }
  }

  return {
    updateAttendanceStatus,
    promoteSpare,
    demoteSpare,
    withdrawSpare,
    updatePlayerNumber,
    updateStartingChange,
  }
}
